import { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import moment from 'moment'
import Icon from '@/components/Icon/Icon'
import Popover from '@/components/Popover'

/**
 * A single-date picker.
 *
 * The prop surface matches pitstop-ui's DatePicker — `selectDate` /
 * `setSelectDate` / `placeHolderText` / `dateFormat` — so a component ported
 * from there needs no rewiring. The calendar itself is built here rather than
 * pulled from react-datepicker: this app carries its own Tailwind components
 * (Input, Dropdown, Popover) and no third-party stylesheet, and a date range is
 * the only thing that needs one.
 *
 * `dateFormat` is a moment format, not date-fns'.
 */
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY'

/**
 * The six-week grid a month is drawn on, starting on the Sunday of the week the
 * 1st falls in. Fixed at six rows so the panel does not change height between
 * months, which would move the day under the pointer as the user pages through.
 */
const monthGrid = monthStart => {
  const firstCell = monthStart.clone().startOf('month').startOf('week')
  return Array.from({ length: 42 }, (_, index) => firstCell.clone().add(index, 'day'))
}

const DatePicker = ({
  selectDate = null,
  setSelectDate,
  placeHolderText = 'Date',
  dateFormat = DEFAULT_DATE_FORMAT,
  disabled = false,
  dataTestId,
  id,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  // The month on screen, which is not the selection: paging through months must
  // not change what is picked.
  const [visibleMonth, setVisibleMonth] = useState(() =>
    (selectDate ? moment(selectDate) : moment()).startOf('month')
  )
  const containerRef = useRef(null)

  // Reopening on a different selection should land on that date's month rather
  // than wherever the user last paged to.
  useEffect(() => {
    if (isOpen && selectDate) setVisibleMonth(moment(selectDate).startOf('month'))
  }, [isOpen, selectDate])

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) setIsOpen(false)
    }
    const handleEscape = event => {
      if (event.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  const days = useMemo(() => monthGrid(visibleMonth), [visibleMonth])
  const selected = selectDate ? moment(selectDate) : null
  const today = moment()

  const pick = day => {
    setSelectDate?.(day.toDate())
    setIsOpen(false)
  }

  const clear = event => {
    event.stopPropagation()
    setSelectDate?.(null)
  }

  return (
    <div className="w-full" ref={containerRef}>
      {/* Positioned by Popover (react-popper) rather than by an absolute div of
          our own, as MultiSelectCheckbox and Profile already do. `strategy:
          'fixed'` is the reason it matters: a calendar rendered inside a
          scrolling ancestor — the listing's table is `overflow-x-auto` — would be
          clipped by it under absolute positioning. Fixed takes the panel out of
          that box, and popper flips it above the field when there is no room
          below. The panel still lives inside containerRef, so the
          click-outside-to-close check is unaffected. */}
      <Popover
        visible={isOpen}
        options={{
          strategy: 'fixed',
          modifiers: [{ name: 'offset', options: { offset: [0, 4] } }],
        }}
      >
        <Popover.Trigger>
          {/* The trigger takes the same tokens Input uses for a filter dropdown —
          36px tall, 4px radius, grey-80 border, grey-50 placeholder, 14px/11px
          padding — so the two pickers read as two more filters in the row rather
          than as a different kind of control. That is exactly what pitstop's own
          date-range override sets out to do ("resized to match the other filters
          (36px, $grey80 border)"), reached here through this app's input tokens
          rather than by overriding a third-party picker's defaults. */}
          <button
            type="button"
            id={id}
            data-testid={dataTestId}
            disabled={disabled}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            onClick={() => setIsOpen(open => !open)}
            className={clsx(
              'font-avenir type-body-2 box-border flex h-9 w-full items-center gap-2 rounded border py-0 pl-3.5 pr-[11px] text-left',
              disabled
                ? 'cursor-not-allowed border-grey-90 bg-grey-95 text-grey-80'
                : 'cursor-pointer border-grey-80 bg-white hover:border-grey-70',
              isOpen && !disabled && 'border-theme-50'
            )}
          >
            {/* Via Icon rather than the SVG directly, the same way InputDropdown
            draws its chevron — so the calendar and the neighbouring chevrons
            share one colour and size. */}
            <Icon name="calendar" style="outlined" size="small" disabled={disabled} />
            <span className={clsx('flex-1 truncate', selected ? 'text-grey-10' : 'text-grey-50')}>
              {selected ? selected.format(dateFormat) : placeHolderText}
            </span>
            {selected && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                aria-label="Clear date"
                data-testid={dataTestId ? `${dataTestId}-clear` : 'date-picker-clear'}
                onClick={clear}
                onKeyDown={event => event.key === 'Enter' && clear(event)}
                className="text-grey-50 hover:text-grey-10 shrink-0 px-1 leading-none"
              >
                ×
              </span>
            )}
          </button>
        </Popover.Trigger>

        <Popover.Content>
          <div
            role="dialog"
            className="border-grey-90 w-[17.5rem] rounded-xl border bg-white p-3 shadow-lg"
          >
            <div className="mb-2 flex items-center justify-between">
              <button
                type="button"
                aria-label="Previous month"
                className="text-grey-50 hover:text-grey-10 h-6 w-6 leading-none"
                onClick={() => setVisibleMonth(month => month.clone().subtract(1, 'month'))}
              >
                ‹
              </button>
              <div className="font-avenir-medium type-body-2 text-grey-10">
                {visibleMonth.format('MMMM YYYY')}
              </div>
              <button
                type="button"
                aria-label="Next month"
                className="text-grey-50 hover:text-grey-10 h-6 w-6 leading-none"
                onClick={() => setVisibleMonth(month => month.clone().add(1, 'month'))}
              >
                ›
              </button>
            </div>

            <div className="text-grey-50 type-micro-heavy grid grid-cols-7 gap-0.5 text-center">
              {WEEKDAYS.map(day => (
                <div key={day} className="py-1">
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-0.5">
              {days.map(day => {
                const isOtherMonth = !day.isSame(visibleMonth, 'month')
                const isSelected = selected?.isSame(day, 'day')
                return (
                  <button
                    key={day.format('YYYY-MM-DD')}
                    type="button"
                    onClick={() => pick(day)}
                    className={clsx(
                      'type-body-2 h-8 w-8 rounded-full',
                      isSelected && 'bg-theme-50 text-white',
                      !isSelected && isOtherMonth && 'text-grey-70 hover:bg-grey-95',
                      !isSelected && !isOtherMonth && 'text-grey-10 hover:bg-grey-95',
                      !isSelected && day.isSame(today, 'day') && 'border-theme-50 border'
                    )}
                  >
                    {day.date()}
                  </button>
                )
              })}
            </div>
          </div>
        </Popover.Content>
      </Popover>
    </div>
  )
}

DatePicker.propTypes = {
  selectDate: PropTypes.instanceOf(Date),
  setSelectDate: PropTypes.func,
  placeHolderText: PropTypes.string,
  // A moment format string.
  dateFormat: PropTypes.string,
  disabled: PropTypes.bool,
  dataTestId: PropTypes.string,
  id: PropTypes.string,
}

export default DatePicker
